import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { BetEventModel, BetOutcomeModel } from '@shared/types';
import { deepCopyMap } from '@shared/utils';

import { BetSystemState } from './types';

const initialState: BetSystemState = {
  betEventOutcomesMap: new Map(),
  betEvents: [],
  processingBetEvent: {} as BetEventModel,
  processingBetOutcome: {} as BetOutcomeModel,
  isBetEventCreateModalOpened: false,
  isBetEventEditModalOpened: false,
  isBetOutcomeCreateModalOpened: false,
  isBetOutcomeEditModalOpened: false,
  isBetEventDeleteModalOpened: false,
  isBetEventFinishModalOpened: false,
  isBetEventUnfinishModalOpened: false,
  isBetOutcomeDeleteModalOpened: false,
  isExportWinStreaksModalOpened: false,
};

export const betSystemSlice = createSlice({
  name: 'betSystemSlice',
  initialState,
  reducers: {
    setBetEvents: (state: BetSystemState, { payload }: PayloadAction<BetEventModel[]>) => {
      state.betEvents = payload;
    },
    updateBetEvent: (state: BetSystemState, { payload }: PayloadAction<BetEventModel>) => {
      const mappedBetEvents = state.betEvents.map((betEvent) => {
        const { id: betEventId } = betEvent;
        const { id: payloadBetEventId } = payload;

        if (betEventId === payloadBetEventId) {
          return payload;
        }

        return betEvent;
      });

      state.betEvents = mappedBetEvents;
    },
    setBetEvent: (state: BetSystemState, { payload }: PayloadAction<BetEventModel>) => {
      state.betEvents = [payload, ...state.betEvents];
    },
    deleteBetEvent: (state: BetSystemState, { payload: payloadBetEventId }: PayloadAction<string>) => {
      const filteredBetEvents = state.betEvents.filter((betEvent) => {
        const { id: betEventId } = betEvent;

        return payloadBetEventId !== betEventId;
      });

      state.betEvents = filteredBetEvents;
    },
    setBetOutcomes: (
      state: BetSystemState,
      {
        payload,
      }: PayloadAction<{
        betEventOutcomes: BetOutcomeModel[];
        betEventId: string;
      }>
    ) => {
      const { betEventId, betEventOutcomes } = payload;

      const copiedMap = deepCopyMap<string, BetOutcomeModel[]>(state.betEventOutcomesMap);

      copiedMap.set(betEventId, betEventOutcomes);

      state.betEventOutcomesMap = copiedMap;
    },
    updateBetOutcome: (state: BetSystemState, { payload }: PayloadAction<BetOutcomeModel>) => {
      const { eventId } = payload;

      const copiedMap = deepCopyMap<string, BetOutcomeModel[]>(state.betEventOutcomesMap);
      const betEventOutcomes = copiedMap.get(eventId) ?? [];

      const mappedBetOutcomes = betEventOutcomes.map((betOutcome) => {
        const { id: betOutcomeId } = betOutcome;
        const { id: payloadBetOutcomeId } = payload;

        if (betOutcomeId === payloadBetOutcomeId) {
          return payload;
        }

        return betOutcome;
      });

      copiedMap.set(eventId, mappedBetOutcomes);

      state.betEventOutcomesMap = copiedMap;
    },
    setBetOutcome: (state: BetSystemState, { payload }: PayloadAction<BetOutcomeModel>) => {
      const { eventId } = payload;

      const copiedMap = deepCopyMap<string, BetOutcomeModel[]>(state.betEventOutcomesMap);
      const betEventOutcomes = copiedMap.get(eventId) ?? [];

      const copiedBetEventOutcomes = [...betEventOutcomes, payload];

      copiedMap.set(eventId, copiedBetEventOutcomes);

      state.betEventOutcomesMap = copiedMap;
    },
    deleteBetOutcome: (
      state: BetSystemState,
      { payload }: PayloadAction<{ betOutcomeId: string; betEventId: string }>
    ) => {
      const { betEventId, betOutcomeId } = payload;

      const copiedMap = deepCopyMap<string, BetOutcomeModel[]>(state.betEventOutcomesMap);
      const betEventOutcomes = copiedMap.get(betEventId) ?? [];

      const filteredBetOutcomes = betEventOutcomes.filter((betOutcome) => {
        const { id: currentBetOutcomeId } = betOutcome;

        return betOutcomeId !== currentBetOutcomeId;
      });

      copiedMap.set(betEventId, filteredBetOutcomes);

      state.betEventOutcomesMap = copiedMap;
    },
    setProcessingBetEvent: (state: BetSystemState, { payload }: PayloadAction<BetEventModel>) => {
      state.processingBetEvent = payload;
    },
    setProcessingBetOutcome: (state: BetSystemState, { payload }: PayloadAction<BetOutcomeModel>) => {
      state.processingBetOutcome = payload;
    },
    setIsBetEventCreateModalOpened: (state: BetSystemState, { payload }: PayloadAction<boolean>) => {
      state.isBetEventCreateModalOpened = payload;
    },
    setIsBetEventEditModalOpened: (state: BetSystemState, { payload }: PayloadAction<boolean>) => {
      state.isBetEventEditModalOpened = payload;
    },
    setIsBetEventDeleteModalOpened: (state: BetSystemState, { payload }: PayloadAction<boolean>) => {
      state.isBetEventDeleteModalOpened = payload;
    },
    setIsBetEventFinishModalOpened: (state: BetSystemState, { payload }: PayloadAction<boolean>) => {
      state.isBetEventFinishModalOpened = payload;
    },
    setIsBetEventUnfinishModalOpened: (state: BetSystemState, { payload }: PayloadAction<boolean>) => {
      state.isBetEventUnfinishModalOpened = payload;
    },
    setIsBetOutcomeDeleteModalOpened: (state: BetSystemState, { payload }: PayloadAction<boolean>) => {
      state.isBetOutcomeDeleteModalOpened = payload;
    },
    setIsBetOutcomeCreateModalOpened: (state: BetSystemState, { payload }: PayloadAction<boolean>) => {
      state.isBetOutcomeCreateModalOpened = payload;
    },
    setIsBetOutcomeEditModalOpened: (state: BetSystemState, { payload }: PayloadAction<boolean>) => {
      state.isBetOutcomeEditModalOpened = payload;
    },
    setIsExportWinStreaksModalOpened: (state: BetSystemState, { payload }: PayloadAction<boolean>) => {
      state.isExportWinStreaksModalOpened = payload;
    },
  },
});

export const {
  setBetEvents,
  setBetEvent,
  updateBetEvent,
  deleteBetEvent,
  setBetOutcomes,
  setBetOutcome,
  deleteBetOutcome,
  updateBetOutcome,
  setProcessingBetEvent,
  setProcessingBetOutcome,
  setIsBetOutcomeEditModalOpened,
  setIsBetOutcomeCreateModalOpened,
  setIsBetOutcomeDeleteModalOpened,
  setIsBetEventFinishModalOpened,
  setIsBetEventUnfinishModalOpened,
  setIsBetEventDeleteModalOpened,
  setIsBetEventCreateModalOpened,
  setIsBetEventEditModalOpened,
  setIsExportWinStreaksModalOpened,
} = betSystemSlice.actions;

export const betSystemSliceReducer = betSystemSlice.reducer;
